import { memo, useMemo, useRef } from 'react';

import { Permission } from '@/entities/permissions';
import ChartShell from '@/features/_shared/chart/ChartShell';
import { ChartProvider } from '@/features/_shared/chart/context/ChartContext';
import useChartFunctions, {
  getStandardWatermarkAttributes,
  getWatermarkConfig,
  mergeChartEvents
} from '@/features/_shared/chart/hooks/options/useChartFunctions';
import { createChartAppearance } from '@/features/_shared/chart/theme/chartAppearance';
import type { ExtendedOptions } from '@/features/_shared/chart/types/highcharts';
import { usePermission } from '@/features/_shared/user';
import type { StaticDataChartWidgetData } from '@/features/_shared/widget-registry';
import { deepMerge } from '@/shared/lib/array/deepMerge';
import { PageInformationContext } from '@/shared/lib/PageInformationContext';
import { useTheme } from '@/shared/ui';
import { Box } from '@/shared/ui/layout/Box';

import type { ChartEventsOptions } from 'highcharts';
import type Highcharts from 'highcharts/highstock';

const applyStaticDataChartOptions = ({
  options,
  events,
  exportEvents
}: {
  options: ExtendedOptions;
  events: ChartEventsOptions;
  exportEvents: ChartEventsOptions;
}): ExtendedOptions => ({
  ...options,
  chart: {
    ...options.chart,
    events: mergeChartEvents(options.chart?.events, events)
  },
  exporting: {
    ...options.exporting,
    enabled: false,
    buttons: {
      ...options.exporting?.buttons,
      contextButton: {
        ...options.exporting?.buttons?.contextButton,
        enabled: false
      }
    },
    chartOptions: {
      ...options.exporting?.chartOptions,
      chart: {
        ...options.exporting?.chartOptions?.chart,
        events: mergeChartEvents(options.exporting?.chartOptions?.chart?.events, exportEvents)
      }
    }
  },
  navigation: {
    ...options.navigation,
    buttonOptions: {
      ...options.navigation?.buttonOptions,
      enabled: false
    }
  }
});

export const StaticDataChart = memo(({ data }: { data: StaticDataChartWidgetData }) => {
  const theme = useTheme();
  const { hasPermission } = usePermission();
  const useSmallWatermark = hasPermission(Permission.UseSmallChartWatermark);
  const { createEventOptions } = useChartFunctions();
  const watermarkImage = useRef<Highcharts.SVGElement | null>(null);
  const appearance = useMemo(() => createChartAppearance(theme, 'regular'), [theme]);
  const { aspectRatio } = getWatermarkConfig(appearance.watermarkName);

  const { events, exportEvents } = useMemo(
    () =>
      createEventOptions(
        watermarkImage,
        chart => getStandardWatermarkAttributes({ chart, aspectRatio, useSmallWatermark }),
        appearance.watermarkName
      ),
    [appearance.watermarkName, aspectRatio, createEventOptions, useSmallWatermark]
  );

  const options = useMemo<ExtendedOptions>(() => {
    const base: ExtendedOptions = {
      accessibility: { enabled: false },
      credits: { enabled: false },
      stockTools: { gui: { enabled: false } },
      chart: {
        animation: false,
        backgroundColor: theme.palette.gx.tileDark,
        style: { color: theme.palette.gx.text, fontFamily: 'Cairo, sans-serif' }
      },
      xAxis: { labels: { style: { color: theme.palette.gx.text } } },
      yAxis: { title: { text: '' }, labels: { style: { color: theme.palette.gx.text } } }
    };
    return applyStaticDataChartOptions({
      options: deepMerge(base, data.chartConfig),
      events,
      exportEvents
    });
  }, [data.chartConfig, events, exportEvents, theme]);

  return (
    <PageInformationContext value={{ mainPageTitle: 'AI Chat' }}>
      <ChartProvider>
        <Box sx={{ width: '100%' }}>
          <ChartShell
            isStockChart={false}
            options={options}
          />
        </Box>
      </ChartProvider>
    </PageInformationContext>
  );
});

StaticDataChart.displayName = 'StaticDataChart';
